import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Layouts
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';
import CustomerLayout from './layouts/CustomerLayout';
import ContentLayout from './layouts/ContentLayout';

// Pages - Public & Essential
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Verify2FA from './pages/Verify2FA';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import CheckoutResult from './pages/CheckoutResult';
import Wishlist from './pages/Wishlist';

// Pages - Content Pages (Direct import for instant load & zero 404s)
import About from './pages/About';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import KVKK from './pages/KVKK';
import Shipping from './pages/Shipping';
import Returns from './pages/Returns';
import FAQ from './pages/FAQ';
import DistanceSalesAgreement from './pages/DistanceSalesAgreement';
import QualityEnvironmentPolicy from './pages/QualityEnvironmentPolicy';
import CookiePolicy from './pages/CookiePolicy';
import InformationSocietyServices from './pages/InformationSocietyServices';
import CompliancePolicy from './pages/CompliancePolicy';
import PrivacyAndSecurity from './pages/PrivacyAndSecurity';
import OrderTracking from './pages/OrderTracking';
import PaymentOptions from './pages/PaymentOptions';
import TransactionGuide from './pages/TransactionGuide';
import SiteMap from './pages/SiteMap';
import RingSizeGuide from './pages/RingSizeGuide';
import DiamondExchange from './pages/DiamondExchange';
import GiftGuide from './pages/GiftGuide';
import Stories from './pages/Stories';
import Stores from './pages/Stores';
import Careers from './pages/Careers';

// Pages - Lazy Customer
const CustomerDashboard = lazy(() => import('./pages/customer/Dashboard'));
const CustomerOrders = lazy(() => import('./pages/customer/Orders'));
const CustomerOrderDetail = lazy(() => import('./pages/customer/OrderDetail'));
const CustomerProfile = lazy(() => import('./pages/customer/Profile'));
const CustomerAddresses = lazy(() => import('./pages/customer/Addresses'));
const CustomerAffiliateDashboard = lazy(() => import('./pages/customer/AffiliateDashboard'));

// Pages - Lazy Admin
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminReports = lazy(() => import('./pages/admin/Reports'));
const AdminProducts = lazy(() => import('./pages/admin/Products'));
const AdminProductForm = lazy(() => import('./pages/admin/ProductForm'));
const AdminCategories = lazy(() => import('./pages/admin/Categories'));
const AdminCollections = lazy(() => import('./pages/admin/Collections'));
const AdminCampaigns = lazy(() => import('./pages/admin/Campaigns'));
const AdminOrders = lazy(() => import('./pages/admin/Orders'));
const AdminOrderDetail = lazy(() => import('./pages/admin/OrderDetail'));
const AdminCustomers = lazy(() => import('./pages/admin/Customers'));
const AdminReviews = lazy(() => import('./pages/admin/Reviews'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminSecurity = lazy(() => import('./pages/admin/SecurityDashboard'));
const FirewallChallenge = lazy(() => import('./pages/FirewallChallenge'));

// Error Pages
const NotFound = lazy(() => import('./pages/errors/NotFound'));
const Unauthorized = lazy(() => import('./pages/errors/Unauthorized'));
const Forbidden = lazy(() => import('./pages/errors/Forbidden'));
const ServerError = lazy(() => import('./pages/errors/ServerError'));
const NotImplemented = lazy(() => import('./pages/errors/NotImplemented'));

// Components
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ScrollToTop from './components/ScrollToTop';

import ThemeController from './components/ThemeController';

function App() {
  return (
    <Router>
      <ThemeController />
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<MainLayout />}>
            <Route index element={<Home />} />
            <Route path="products" element={<Products />} />
            <Route path="products/:slug" element={<ProductDetail />} />
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password/:token" element={<ResetPassword />} />
            <Route path="verify-email/:verificationtoken" element={<VerifyEmail />} />
            <Route path="verify-2fa" element={<Verify2FA />} />
            <Route path="cart" element={<Cart />} />
            <Route path="wishlist" element={<Wishlist />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="checkout/result" element={<CheckoutResult />} />

            {/* Content Pages with Sidebar */}
            <Route element={<ContentLayout />}>
              <Route path="about" element={<About />} />
              <Route path="contact" element={<Contact />} />
              <Route path="terms" element={<Terms />} />
              <Route path="privacy" element={<Privacy />} />
              <Route path="kvkk" element={<KVKK />} />
              <Route path="shipping" element={<Shipping />} />
              <Route path="returns" element={<Returns />} />
              <Route path="faq" element={<FAQ />} />
              <Route path="distance-sales-agreement" element={<DistanceSalesAgreement />} />
              <Route path="quality-environment-policy" element={<QualityEnvironmentPolicy />} />
              <Route path="cookie-policy" element={<CookiePolicy />} />
              <Route path="information-society-services" element={<InformationSocietyServices />} />
              <Route path="compliance-policy" element={<CompliancePolicy />} />
              <Route path="privacy-security" element={<PrivacyAndSecurity />} />
              <Route path="order-tracking" element={<OrderTracking />} />
              <Route path="payment-options" element={<PaymentOptions />} />
              <Route path="transaction-guide" element={<TransactionGuide />} />
              <Route path="site-map" element={<SiteMap />} />
              <Route path="ring-size-guide" element={<RingSizeGuide />} />
              <Route path="diamond-exchange" element={<DiamondExchange />} />

              {/* New Added Pages */}
              <Route path="gift-guide" element={<GiftGuide />} />
              <Route path="stories" element={<Stories />} />
              <Route path="stores" element={<Stores />} />
              <Route path="careers" element={<Careers />} />
            </Route>
          </Route>

          {/* Customer Routes */}
          <Route path="/account" element={
            <ProtectedRoute>
              <CustomerLayout />
            </ProtectedRoute>
          }>
            <Route index element={<CustomerDashboard />} />
            <Route path="orders" element={<CustomerOrders />} />
            <Route path="orders/:id" element={<CustomerOrderDetail />} />
            <Route path="profile" element={<CustomerProfile />} />
            <Route path="addresses" element={<CustomerAddresses />} />
            <Route path="affiliate" element={<CustomerAffiliateDashboard />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/new" element={<AdminProductForm />} />
            <Route path="products/edit/:id" element={<AdminProductForm />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="collections" element={<AdminCollections />} />
            <Route path="campaigns" element={<AdminCampaigns />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:id" element={<AdminOrderDetail />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="security" element={<AdminSecurity />} />
          </Route>

          {/* Firewall Challenge Route */}
          <Route path="/security-challenge" element={<FirewallChallenge />} />

          {/* Error Routes */}
          <Route path="/401" element={<Unauthorized />} />
          <Route path="/403" element={<Forbidden />} />
          <Route path="/500" element={<ServerError />} />
          <Route path="/501" element={<NotImplemented />} />

          {/* 404 - Catch all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
